/* assets/cbi-scheme.js
 * Page fade between section colour schemes.
 *
 * This runs on a page that carries data-cbi-scheme-page, which today is the
 * Interiors index and nothing else. There a visitor sees one project at a
 * time, so the page itself changes colour. Elsewhere, on About and the rest,
 * the sections simply paint their own backgrounds and nothing is taken over.
 *
 * Every element with data-cbi-scheme declares a background and text colour
 * through --cbi-section-bg and --cbi-section-fg. The active scheme is the last
 * one, in document order, whose top has crossed the trigger line: a share of
 * the viewport height set in theme settings (data-cbi-fade-trigger on body).
 * Above the first scheme section, the first one applies. The colours are set
 * on <body>, where cbi-base.css transitions them, and the sections themselves
 * go transparent.
 *
 * Scheme sections that are not rendered (display: none, such as the hidden
 * view on the Interiors page) or carry data-cbi-scheme-off (the slides not
 * showing on the Interiors stage) are skipped.
 *
 * One rect read per scheme section per animation frame, from a passive scroll
 * listener. Pages without scheme sections are left untouched.
 */

(() => {
  const root = document.documentElement;
  let sections = [];
  let current = null;
  let ticking = false;

  const triggerShare = () => {
    const share = parseFloat(document.body.dataset.cbiFadeTrigger);
    return (Number.isNaN(share) ? 75 : share) / 100;
  };

  const colours = (el) => {
    const style = getComputedStyle(el);
    return {
      bg: style.getPropertyValue('--cbi-section-bg').trim(),
      fg: style.getPropertyValue('--cbi-section-fg').trim(),
    };
  };

  const apply = () => {
    ticking = false;
    if (!sections.length) return;
    const line = window.innerHeight * triggerShare();
    const shown = sections.filter((el) => !('cbiSchemeOff' in el.dataset) && el.getClientRects().length);
    if (!shown.length) return;
    let active = shown[0];
    for (const el of shown) {
      if (el.getBoundingClientRect().top <= line) active = el;
    }
    if (active === current) return;
    current = active;
    const { bg, fg } = colours(active);
    if (bg) document.body.style.backgroundColor = bg;
    if (fg) document.body.style.color = fg;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  };

  /* The header is a transparent row of its own at the top of every template but
   * the homepage, so whatever the body is painted shows through it, and so is
   * the overscroll at either end. The body takes the first scheme section's
   * background, which is what the page opens on, rather than the default
   * ground: otherwise the strip behind the bar reads as a band of its own
   * colour above the page (Kurt). Nothing else is affected, since every
   * section paints itself. */
  const ground = () => {
    const first = document.querySelector('[data-cbi-scheme]');
    const bg = first ? colours(first).bg : '';
    if (bg) {
      document.body.style.backgroundColor = bg;
    } else {
      document.body.style.removeProperty('background-color');
    }
  };

  const collect = () => {
    /* Only a page that asks for it, with data-cbi-scheme-page, hands its
     * colours to the body. On every other page the scheme sections keep their
     * own backgrounds and simply follow each other down the page (Kurt). */
    sections = document.querySelector('[data-cbi-scheme-page]')
      ? Array.from(document.querySelectorAll('[data-cbi-scheme]'))
      : [];
    current = null;
    if (!sections.length) {
      root.classList.remove('cbi-fade-ready', 'cbi-fade-instant');
      document.body.style.removeProperty('color');
      ground();
      return;
    }
    /* First application without a transition, then enable fading. */
    root.classList.add('cbi-fade-ready', 'cbi-fade-instant');
    apply();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => root.classList.remove('cbi-fade-instant'));
    });
  };

  const start = () => {
    collect();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    /* A back or forward cache restore keeps old colours for a new scroll
     * position, so resolve again without animating. */
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) collect();
    });
    /* Theme editor: sections are added, removed, reordered and re-rendered in
     * place, and colour settings change without a page reload. */
    for (const name of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
      document.addEventListener(name, () => window.requestAnimationFrame(collect));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
